<template>
  <div class="min-h-screen flex items-center justify-center relative p-4 py-8 bg-slate-50/70 dark:bg-[#070c18] overflow-hidden">
    <!-- Ambient Glow / Mesh Background Accents -->
    <div class="absolute -top-32 -left-32 w-96 h-96 bg-indigo-500/15 dark:bg-indigo-600/10 rounded-full blur-3xl pointer-events-none"></div>
    <div class="absolute -bottom-32 -right-32 w-96 h-96 bg-purple-500/15 dark:bg-purple-600/10 rounded-full blur-3xl pointer-events-none"></div>
    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-500/5 dark:bg-violet-500/5 rounded-full blur-3xl pointer-events-none"></div>

    <!-- Theme Toggle -->
    <div class="absolute top-4 right-4 z-50">
      <ThemeToggle />
    </div>

    <div class="w-full max-w-[440px] relative z-10">
      <!-- Login Card -->
      <div
        class="bg-white/95 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl border border-slate-200/80 dark:border-slate-800/80 overflow-hidden relative transition-all duration-300"
      >
        <!-- Top Gradient Hairline -->
        <div class="h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>

        <!-- Google Processing Overlay -->
        <transition name="fade">
          <div
            v-if="isGoogleProcessing"
            class="absolute inset-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm flex items-center justify-center z-50 p-6 text-center"
          >
            <div class="flex flex-col items-center">
              <div class="w-10 h-10 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin mb-3"></div>
              <p class="text-sm font-semibold text-slate-800 dark:text-slate-200">
                Authenticating with Google...
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Verifying your credentials securely
              </p>
            </div>
          </div>
        </transition>

        <!-- Header -->
        <LoginHeader />

        <!-- Account Not Found Modal -->
        <AccountNotFoundModal
          :show="showAccountNotFoundModal"
          :email="accountNotFoundEmail"
          @close="closeAccountNotFoundModal"
        />

        <!-- Card Body -->
        <div class="px-6 sm:px-8 pb-8 pt-1">
          <!-- Segmented Tab Switcher (OTP vs Password) -->
          <div
            class="grid grid-cols-2 p-1 bg-slate-100/80 dark:bg-slate-800/80 rounded-2xl border border-slate-200/70 dark:border-slate-700/60 mb-5"
          >
            <button
              type="button"
              @click="isOtpMode = true"
              :class="[
                'py-2 px-3 text-xs sm:text-sm font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-1.5',
                isOtpMode
                  ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 font-bold border border-slate-200/60 dark:border-slate-600/60'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
              ]"
            >
              <Smartphone class="w-3.5 h-3.5" />
              <span>OTP Instant</span>
            </button>

            <button
              type="button"
              @click="isOtpMode = false"
              :class="[
                'py-2 px-3 text-xs sm:text-sm font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-1.5',
                !isOtpMode
                  ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 font-bold border border-slate-200/60 dark:border-slate-600/60'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
              ]"
            >
              <KeyRound class="w-3.5 h-3.5" />
              <span>Password</span>
            </button>
          </div>

          <!-- Forms Block -->
          <div>
            <!-- OTP Login Mode -->
            <OtpLogin
              v-if="isOtpMode"
              @success="handleOtpSuccess"
              @cancel="isOtpMode = false"
            />

            <!-- Password Login Mode -->
            <LoginForm
              v-else
              :error="errorMessage"
              :is-loading="isLoading"
              @submit="handleLogin"
              @update:error="errorMessage = $event"
            />
          </div>

          <!-- Google OAuth Block -->
          <div class="mt-5">
            <Divider text="Or connect with Google" />

            <!-- Stable Google Placeholders (single instance, fixed IDs) -->
            <div class="flex flex-col items-center justify-center min-h-[44px] pt-1">
              <div id="g_id_onload"></div>
              <div id="g_id_signin" class="w-full flex justify-center"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Signup Navigation Link -->
      <div class="text-center mt-5">
        <p class="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
          Don’t have an institution account?
          <RouterLink
            to="/signup"
            class="font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors ml-1"
          >
            Create School Account &rarr;
          </RouterLink>
        </p>
      </div>

      <!-- Security Footer -->
      <LoginFooter />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import jwt_decode from "jwt-decode";
import { RouterLink } from "vue-router";
import { Smartphone, KeyRound } from "lucide-vue-next";

// Components
import LoginHeader from "@/components/Auth/LoginHeader.vue";
import AccountNotFoundModal from "@/components/Auth/AccountNotFoundModal.vue";
import Divider from "@/components/Auth/Divider.vue";
import LoginForm from "@/components/Auth/LoginForm.vue";
import LoginFooter from "@/components/Auth/LoginFooter.vue";
import OtpLogin from "@/components/Auth/OtpLogin.vue";
import ThemeToggle from "@/components/Header/ThemeToggle/ThemeToggle.vue";

// Composables
import { useGoogleAuth } from "@/composables/useGoogleAuth";
import { useAuth } from "@/composables/useAuth";
import { useGlobalToast } from "@/utils/useGlobalToast";

const store = useStore();
const { login, redirectToDashboard } = useAuth();
const { showToast } = useGlobalToast();

const {
  initializeGoogleAuth,
  cleanup,
} = useGoogleAuth();

// States
const errorMessage = ref("");
const isLoading = ref(false);
const isGoogleProcessing = ref(false);
const isOtpMode = ref(true);

const showAccountNotFoundModal = ref(false);
const accountNotFoundEmail = ref("");

// Google OAuth Setup
onMounted(async () => {
  await initializeGoogleAuth({
    onSuccess: handleGoogleSuccess,
    onError: (error, email) => handleGoogleError(error, email),
    onProcessingStart: () => {
      isGoogleProcessing.value = true;
    },
  });
});

onUnmounted(() => {
  cleanup();
});

// Handle successful Google authentication
const handleGoogleSuccess = async (result) => {
  try {
    if (result.token) {
      await handleTokenLogin(result.token);
    }
  } finally {
    isGoogleProcessing.value = false;
  }
};

// Handle Google authentication errors
const handleGoogleError = (error, email = null) => {
  console.error("Google authentication error:", error);

  if (error.response?.data?.error) {
    showToast("error", error.response.data.error);
  } else if (
    error.message?.includes("No account found with this email") ||
    error?.response?.status === 404
  ) {
    if (email) {
      accountNotFoundEmail.value = email;
      showAccountNotFoundModal.value = true;
    } else {
      showToast("error", "No account found. Please register first.");
    }
  } else {
    showToast("error", "Google authentication failed. Please try again.");
  }

  isGoogleProcessing.value = false;
};

// Handle Password Login
const handleLogin = async (credentials) => {
  try {
    isLoading.value = true;
    errorMessage.value = "";

    const result = await login(credentials);
    if (result?.token) {
      await handleTokenLogin(result.token);
    }
  } catch (err) {
    console.error("Login error:", err);

    if (
      err.response?.status === 404 &&
      err.response?.data?.error?.includes("No account found")
    ) {
      accountNotFoundEmail.value = credentials.email;
      showAccountNotFoundModal.value = true;
    } else {
      errorMessage.value =
        err.response?.data?.error || "Login failed. Please try again.";
    }
  } finally {
    isLoading.value = false;
  }
};

// Handle OTP Login Success
const handleOtpSuccess = async ({ token }) => {
  try {
    await handleTokenLogin(token);
  } catch (err) {
    console.error("[OTP] Post-login error:", err);
  }
};

// Shared Login Logic
const handleTokenLogin = async (token) => {
  localStorage.setItem("token", token);
  const decoded = jwt_decode(token);

  if (decoded.schoolId) store.commit("SET_SCHOOL_ID", decoded.schoolId);
  if (decoded.role) store.commit("SET_USER_ROLE", decoded.role);
  if (decoded.userId) store.commit("SET_USER_ID", decoded.userId);
  if (decoded.name) store.commit("SET_USER_NAME", decoded.name);

  await store.dispatch("initializeApp");
  redirectToDashboard();
};

// Close Account Not Found Modal
const closeAccountNotFoundModal = () => {
  showAccountNotFoundModal.value = false;
  accountNotFoundEmail.value = "";
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

#g_id_onload {
  position: relative;
}

#g_id_signin {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
